// Geometry helpers for the choropleths (d3-geo).
import { geoArea } from 'd3-geo';
import type { Polygon, Position } from 'geojson';
import type { GeoCollection, GeoFeature } from './store';

/** d3-geo wants clockwise exterior rings; RFC 7946 files are counter-clockwise. Fix either way. */
function normalizeWinding(f: GeoFeature): GeoFeature {
  const g = f.geometry;
  const fixPoly = (rings: Position[][]): Position[][] => {
    const poly: Polygon = { type: 'Polygon', coordinates: rings };
    return geoArea(poly) > 2 * Math.PI ? rings.map((r) => [...r].reverse()) : rings;
  };
  if (g.type === 'Polygon') return { ...f, geometry: { type: 'Polygon', coordinates: fixPoly(g.coordinates) } };
  if (g.type === 'MultiPolygon') return { ...f, geometry: { type: 'MultiPolygon', coordinates: g.coordinates.map(fixPoly) } };
  return f;
}

const cache = new WeakMap<GeoCollection, Map<string, GeoFeature>>();
/** unit_id -> feature, plus fallbacks: '~<lower-cased id>' (ENR precinct names vary in case between sites) and
 *  '=<alt id>' for earlier spellings listed in properties.alt_ids. List shapes with keys lacking '~'/'=' prefixes. */
export function featuresById(geo: GeoCollection): Map<string, GeoFeature> {
  let m = cache.get(geo);
  if (!m) {
    m = new Map();
    for (const f of geo.features) {
      if (!f?.properties?.unit_id || !f.geometry) continue;
      const id = String(f.properties.unit_id);
      const nf = normalizeWinding(f);
      m.set(id, nf);
      if (!m.has(`~${id.toLowerCase()}`)) m.set(`~${id.toLowerCase()}`, nf);
      // earlier spellings of the same precinct ('Town of Seabrook' → 2026 'Seabrook'); see baselines/geo_align.py.
      // Stored under '=' so callers that list every shape (keys without '~'/'=') don't draw a feature twice.
      const alts = (f.properties as { alt_ids?: unknown }).alt_ids;
      if (Array.isArray(alts)) for (const a of alts.map(String)) {
        if (!m.has(`=${a}`)) m.set(`=${a}`, nf);
        if (!m.has(`=~${a.toLowerCase()}`)) m.set(`=~${a.toLowerCase()}`, nf);
      }
    }
    cache.set(geo, m);
  }
  return m;
}
export const featureFor = (byId: Map<string, GeoFeature>, unitId: string) =>
  byId.get(unitId) ?? byId.get(`~${unitId.toLowerCase()}`) ?? byId.get(`=${unitId}`) ?? byId.get(`=~${unitId.toLowerCase()}`);
/** Pseudo-units such as 'Charleston|*other*' (countywide provisional/failsafe) have no geography. */
export const isPseudo = (unitId: string) => unitId.includes('*');

export type MapMode = 'margin' | 'swing_b22' | 'swing_b24' | 'reporting' | 'turnout';
export interface Bin { max: number; mix: number; label: string }
export const MARGIN_BINS: Bin[] = [{ max: 5, mix: 24, label: '<5' }, { max: 10, mix: 42, label: '5–10' }, { max: 20, mix: 62, label: '10–20' }, { max: 30, mix: 80, label: '20–30' }, { max: Infinity, mix: 96, label: '30+' }];
export const SWING_BINS: Bin[] = [{ max: 2, mix: 24, label: '<2' }, { max: 5, mix: 42, label: '2–5' }, { max: 10, mix: 62, label: '5–10' }, { max: 15, mix: 80, label: '10–15' }, { max: Infinity, mix: 96, label: '15+' }];
export const TURNOUT_BINS: Bin[] = [{ max: 0.5, mix: 16, label: '<50%' }, { max: 0.75, mix: 32, label: '50–75%' }, { max: 0.9, mix: 48, label: '75–90%' }, { max: 1.0, mix: 64, label: '90–100%' }, { max: 1.1, mix: 80, label: '100–110%' }, { max: Infinity, mix: 96, label: '110%+' }];
export const mapMix = (c: string, pct: number) => `color-mix(in oklab, ${c} ${pct}%, var(--map-base))`;
